import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';

export interface TransferCandidate {
  readonly id: string;
  readonly name: string;
  readonly email?: string;
}

/**
 * Passar o papel de Super administrador a outra pessoa (RN-289).
 *
 * É a única saída do papel: ninguém o revoga nem o desativa, e o sistema nunca fica sem
 * titular. A tela diz o que acontece com quem passa — ele deixa de decidir sobre papéis e
 * permissões, e segue com os outros papéis que tinha —, porque é uma troca que só quem
 * recebe pode devolver.
 *
 * Só aparecem perfis **ativos**: o papel passado a uma conta que ninguém usa deixaria o
 * sistema com titular de nome e sem ninguém capaz de decidir.
 */
export function TransferSuperAdminDialog({
  open,
  onOpenChange,
  candidates,
  isPending,
  errorMessage,
  onSubmit,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  candidates: readonly TransferCandidate[];
  isPending: boolean;
  errorMessage?: string;
  onSubmit: (userId: string) => void;
}) {
  const [userId, setUserId] = useState('');

  useEffect(() => {
    if (open) setUserId('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Transferir o Super administrador</DialogTitle>
          <DialogDescription>
            O papel passa para a pessoa escolhida e sai de você, de uma vez. Você deixa de cuidar
            dos papéis e das permissões, mantém os outros papéis que tem — e só quem o recebe pode
            devolvê-lo. A transferência fica registrada na auditoria.
          </DialogDescription>
        </DialogHeader>

        <Field
          label="Novo Super administrador"
          htmlFor="transfer-user"
          required
          {...(errorMessage === undefined ? {} : { error: errorMessage })}
        >
          <select
            id="transfer-user"
            className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-2 text-sm"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
          >
            <option value="">Escolha um perfil ativo</option>
            {candidates.map((candidate) => (
              <option key={candidate.id} value={candidate.id}>
                {candidate.name}
                {candidate.email === undefined ? '' : ` — ${candidate.email}`}
              </option>
            ))}
          </select>
        </Field>

        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            disabled={isPending || userId === ''}
            onClick={() => onSubmit(userId)}
          >
            {isPending ? 'Transferindo…' : 'Transferir o papel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
